/* Plans that propose a correction, what each would change, and where each one stands.
 *
 * **The table is the screen, as the design draws it.** The two bands that used to sit above it —
 * "this deployment cannot execute a plan" and "flagged is not fixed" — are gone, and neither fact
 * went with them: there is still no Execute control anywhere, and every row still names its flagged
 * findings beside its correctable ones, warn-toned, because "we can fix 3 of 40" is the headline a
 * fix-shaped screen hides. The plan page says the rest where it is acted on.
 *
 * **An unsimulated plan reads "Not simulated", never zero.** Simulated-and-found-nothing is a
 * different fact, and a confident zero would let an approver sign over counts nobody has taken.
 */

import { useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import type { Plan } from '@/shared/api/generated/remediation';
import { ageText } from '@/shared/model/age';
import { personName } from '@/shared/model/person';
import { Alert } from '@/shared/ui/Alert';
import { PageHeader } from '@/shared/ui/PageHeader';
import { SkeletonRows } from '@/shared/ui/Skeleton';

import { usePlanQueue } from '../api/plans';
import {
  asInstant,
  identityOf,
  matchesMode,
  matchesState,
  MODE_FILTERS,
  rowsReading,
  stateLabelKey,
  STATE_FILTERS,
  stateToneOf,
  unfixableCount,
  waitingOnAPerson,
} from '../model/plans';
import type { ModeFilter, StateFilter } from '../model/plans';

function PlanRow({
  plan,
  now,
}: {
  readonly plan: Plan;
  readonly now: Date;
}): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const open = (): void => {
    navigate(`/remediation/${encodeURIComponent(plan.id)}`);
  };

  const flagged = unfixableCount(plan);
  const rows = rowsReading(plan);
  const identity = identityOf(plan);

  return (
    <tr className="lq-rm__row" onClick={open}>
      <td data-label={t('remediation.column.plan')}>
        {/* **The table names the subject; the instant names the row.** Several plans against one
            table is the ordinary case, so the name alone left rows nobody could choose between.
            The id is shortened rather than dropped, and its full 36 characters stay on `title`
            and on the plan page. */}
        <div className="lq-mono lq-rm__relation">
          {identity.relation ?? t('remediation.unnamedRelation')}
        </div>
        <div className="lq-cp__muted">
          <span title={asInstant(identity.madeAt)}>
            {t('remediation.made', {
              when: ageText(t, identity.madeAt, now) ?? asInstant(identity.madeAt),
            })}
          </span>
          {' · '}
          <span className="lq-mono" title={identity.id}>
            {identity.shortId}
          </span>
        </div>
      </td>
      <td data-label={t('remediation.column.state')}>
        <span
          className={`lq-tag lq-tag--rm-${stateToneOf(plan.state)}`}
          title={t(`remediation.stateHint.${plan.state}`)}
        >
          {t(stateLabelKey(plan.state))}
        </span>
      </td>
      <td
        data-label={t('remediation.column.mode')}
        className="lq-nowrap"
        title={t(`remediation.modeNote.${plan.mode}`)}
      >
        {t(`remediation.mode.${plan.mode}`)}
      </td>
      <td data-label={t('remediation.column.findings')} className="lq-nowrap">
        {t('remediation.correctable', { count: plan.applicable_count })}
        {/* Only when there is something to report. Amber on every row, including the rows where
            nothing was flagged, is how a reader learns to stop seeing the colour. */}
        {flagged > 0 && (
          <div className="lq-rm__flagged">
            {t('remediation.flagged', { count: flagged })}
          </div>
        )}
      </td>
      <td data-label={t('remediation.column.rows')} className="lq-nowrap">
        {rows.state === 'unsimulated' ? (
          <span className="lq-muted">{t('remediation.notSimulated')}</span>
        ) : (
          rows.rows.toLocaleString()
        )}
      </td>
      <td
        data-label={t('remediation.column.decided')}
        className="lq-nowrap lq-rm__decided"
      >
        {plan.decided_by === null || plan.decided_at === null ? (
          t('remediation.undecided')
        ) : (
          <span title={`${plan.decided_by} · ${asInstant(plan.decided_at)}`}>
            {t('remediation.decidedBy', {
              who: personName(plan.decided_by),
              age: ageText(t, plan.decided_at, now) ?? asInstant(plan.decided_at),
            })}
          </span>
        )}
      </td>
      <td className="lq-table__actions">
        <button
          type="button"
          className="lq-cp__headButton"
          onClick={(event) => {
            event.stopPropagation();
            open();
          }}
        >
          {t('remediation.open')}
        </button>
      </td>
    </tr>
  );
}

export function RemediationQueue(): ReactElement {
  const { t } = useTranslation();
  const queue = usePlanQueue();
  const [state, setState] = useState<StateFilter>('all');
  const [mode, setMode] = useState<ModeFilter>('all');
  /* One clock for the whole table, so two rows planned in the same minute cannot read as different
     ages because they rendered a tick apart. */
  const [now] = useState(() => new Date());

  const all = queue.isSuccess ? queue.data : [];
  const visible = all.filter(
    (plan) => matchesState(plan, state) && matchesMode(plan, mode),
  );
  const filtering = state !== 'all' || mode !== 'all';

  return (
    <div className="lq-rm">
      <PageHeader
        title={t('remediation.title')}
        help={t('help.feature.remediation')}
        lede={t('remediation.intro')}
        meta={
          queue.isSuccess ? (
            <span>
              {t('remediation.metaLine', {
                count: all.length,
                waiting: waitingOnAPerson(all),
              })}
            </span>
          ) : undefined
        }
      />

      <div className="lq-rm__filters">
        <div
          className="lq-rm__filterGroup"
          role="group"
          aria-label={t('remediation.filter.state')}
        >
          {STATE_FILTERS.map((each) => (
            <button
              key={each}
              type="button"
              className="lq-rm__filter"
              aria-pressed={each === state}
              onClick={() => {
                setState(each);
              }}
            >
              {t(`remediation.filter.${each}`)}
            </button>
          ))}
        </div>
        <select
          className="lq-rm__select"
          aria-label={t('remediation.filter.mode')}
          value={mode}
          onChange={(event) => {
            setMode(event.target.value as ModeFilter);
          }}
        >
          {MODE_FILTERS.map((each) => (
            <option key={each} value={each}>
              {t(`remediation.modeFilter.${each}`)}
            </option>
          ))}
        </select>
      </div>

      {queue.isPending && <SkeletonRows rows={4} columns={7} />}

      {queue.isError && (
        <Alert tone="error" title={t('remediation.loadFailed')}>
          {queue.error.message}
        </Alert>
      )}

      {queue.isSuccess && (
        <section
          className="lq-cp__card lq-cp__card--table"
          aria-label={t('remediation.caption')}
        >
          <div className="lq-cp__scroll">
            <table className="lq-table lq-table--ledger lq-cp__table lq-rm__queue">
              <caption className="lq-visually-hidden">{t('remediation.caption')}</caption>
              <thead>
                <tr>
                  <th scope="col">{t('remediation.column.plan')}</th>
                  <th scope="col">{t('remediation.column.state')}</th>
                  <th scope="col">{t('remediation.column.mode')}</th>
                  <th scope="col">{t('remediation.column.findings')}</th>
                  <th scope="col">{t('remediation.column.rows')}</th>
                  <th scope="col">{t('remediation.column.decided')}</th>
                  <th scope="col" className="lq-table__actions">
                    <span className="lq-visually-hidden">{t('remediation.open')}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {visible.map((plan) => (
                  <PlanRow key={plan.id} plan={plan} now={now} />
                ))}
              </tbody>
            </table>
          </div>
          {visible.length === 0 ? (
            /* Two different sentences under the same header. An empty estate is a fact about the
               tenant; an empty filter is a fact about the filter, and confusing them would report a
               queue with work in it as an empty one. */
            <p className="lq-rm__none">
              {t(filtering ? 'remediation.filtered.title' : 'remediation.empty.title')}{' '}
              {t(filtering ? 'remediation.filtered.body' : 'remediation.empty.body')}
            </p>
          ) : null}
        </section>
      )}
    </div>
  );
}
